import { useState } from "react";
import { X, Send, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import axios from "axios";
import { formatApiError } from "../lib/api";

const inputCls =
  "w-full bg-white border border-[#E8E6E1] rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E5C50]/30 focus:border-[#2E5C50] transition-colors duration-200";

export default function BookDemoDialog({ open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", organization: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/demo-requests`, form);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Something went wrong — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="book-demo-dialog" className="fixed inset-0 z-50 bg-[#1C211F]/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-7 shadow-2xl my-8 relative" onClick={(e) => e.stopPropagation()}>
        <button data-testid="book-demo-close" onClick={onClose} className="absolute top-4 right-4 text-[#57605A] hover:text-[#1C211F]" aria-label="Close">
          <X className="w-5 h-5" strokeWidth={1.5} />
        </button>

        {done ? (
          <div className="py-6 text-center">
            <div className="w-14 h-14 mx-auto bg-[#E6F0EB] rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-[#34745C]" strokeWidth={1.5} />
            </div>
            <h3 className="mt-5 font-heading text-2xl text-[#1C211F]">Demo request received</h3>
            <p className="mt-2 text-sm text-[#57605A]">We'll reach out within 1 business day to schedule a walkthrough tailored to your agency.</p>
            <button data-testid="book-demo-done" onClick={onClose} className="mt-6 bg-[#2E5C50] text-white hover:bg-[#22463D] rounded-md px-6 py-2.5 text-sm font-medium">
              Close
            </button>
          </div>
        ) : (
          <>
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#C07A60]">Book a demo</p>
            <h2 className="mt-2 font-heading text-2xl text-[#1C211F]">See Nexari Health in 15 minutes.</h2>
            <p className="mt-2 text-sm text-[#57605A]">A real walkthrough of compliance scores, reminder cadence, and audit-packet generation — using your real workflow.</p>

            <form onSubmit={submit} className="mt-5 space-y-3">
              <input data-testid="bd-name"  type="text"  required placeholder="Your name"             value={form.name}         onChange={set("name")}         className={inputCls} />
              <input data-testid="bd-email" type="email" required placeholder="Work email"            value={form.email}        onChange={set("email")}        className={inputCls} />
              <input data-testid="bd-org"   type="text"  required placeholder="Agency / organization" value={form.organization} onChange={set("organization")} className={inputCls} />
              <textarea data-testid="bd-msg" rows={3} placeholder="How many employees? Any specific workflows you want to see?" value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} />
              {error && <p className="text-xs text-[#B54D40]">{error}</p>}
              <button data-testid="bd-submit" type="submit" disabled={sending}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] rounded-md px-5 py-3 text-sm font-medium disabled:opacity-60">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.5} />}
                {sending ? "Sending…" : "Request your demo"}
              </button>
              <p className="text-[10px] text-[#57605A] inline-flex items-center gap-1.5 justify-center w-full pt-1">
                <ShieldCheck className="w-3 h-3" strokeWidth={2} /> Your info is used only to schedule this demo.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
